import { authGeneration, authSignal, reportSessionExpired } from '../auth/session'
import type { Role } from '../lib/roles'
export type { Role }

/** One row of `GET /api/admin/users`. */
export interface AdminUser {
  id: string
  email: string
  canCreateCompanies: boolean
  disabled: boolean
  platformAdmin: boolean
  admittedAt: string
  /** Null until the user's first sign-in after migration 0015. */
  lastSignedInAt: string | null
  companies: { id: string; name: string; role: Role }[]
  pendingInvites: { companyId: string; companyName: string; role: Role; expiresAt: string }[]
}

/** An access request, pending or decided. */
export interface AdminRequest {
  id: string
  kind: 'admission' | 'companyCreation'
  email: string
  requestedAt: string
  message: string | null
  /** The requester's companies; empty for an admission request. */
  memberships: { companyName: string; role: Role }[]
  status: 'pending' | 'approved' | 'rejected'
  decidedAt: string | null
  decidedBy: string | null
}

/**
 * What an admin request ended as. `send` only classifies: apart from reporting a `401`, it
 * never navigates or calls back, because what a `denied` means depends on where the caller
 * is (design, "Frontend").
 *
 * - `stale`: the session's generation moved on while the request was out; the answer is
 *   dropped, whatever it was.
 * - `expired`: a `401`, already reported through `reportSessionExpired()`.
 * - `denied`: a `404` with an empty body, the authority layer's answer. The caller has lost
 *   platform authority.
 * - `notFound`: a `404` with JSON `not_found`, a handler's answer for one unknown request
 *   or company, or `unknown_account` for an account discovery no longer lists.
 * - `refused`: a handler's refusal the later E6 screens draw (GRO-135, D9), with the status
 *   it is answered under. `not_implemented` is a route whose slice has not landed yet.
 *   `duplicate_name` names the member whose switcher holds a rename's name, when the body
 *   does (GRO-136, D3), and carries the colliding members of a restore (GRO-138, D6); a
 *   route that names none answers them with `null` and an empty list.
 */
export type Sent<T> =
  | { status: 'ok'; body: T }
  | { status: 'stale' }
  | { status: 'expired' }
  | { status: 'denied' }
  | { status: 'notFound' }
  | { status: 'conflict'; code: 'already_decided'; request: AdminRequest }
  | { status: 'conflict'; code: 'identity_conflict' }
  | { status: 'refused'; code: Exclude<RefusalCode, 'claim_blocked' | 'claimed_elsewhere' | 'duplicate_name'> }
  | { status: 'refused'; code: 'claim_blocked'; accounts: PortfolioAccount[] }
  | { status: 'refused'; code: 'claimed_elsewhere'; company: { id: string; name: string } }
  | { status: 'refused'; code: 'duplicate_name'; member: string | null; collisions: Collision[] }
  | { status: 'unavailable' }

/** A member whose other company already carries a restore's target name. */
export interface Collision {
  email: string
  companyName: string
}

/** The refusal codes of the E6 routes, each with the HTTP status it is answered under. */
const REFUSALS = {
  empty_name: 400,
  duplicate_name: 409,
  reserved_name: 409,
  company_frozen: 409,
  not_frozen: 409,
  no_eligible_owner: 409,
  claim_blocked: 409,
  claimed_elsewhere: 409,
  not_claimed: 409,
  unknown_company: 409,
  demo_company: 409,
  bad_request: 400,
  name_mismatch: 422,
  not_implemented: 501,
} as const
export type RefusalCode = keyof typeof REFUSALS

function refusalOf(status: number, error: unknown, body: unknown): Sent<never> | null {
  if (typeof error !== 'string' || !(error in REFUSALS)) return null
  const code = error as RefusalCode
  if (REFUSALS[code] !== status) return null
  if (code === 'duplicate_name') {
    const fields = record(body)
    const member = isString(fields?.member) ? fields.member : null
    const listed = fields?.collisions
    if (listed === undefined) return { status: 'refused', code, member, collisions: [] }
    const collisions = arrayOf(listed, (c) => {
      const r = record(c)
      return r && isString(r.email) && isString(r.companyName) ? { email: r.email, companyName: r.companyName } : null
    })
    return collisions === null ? null : { status: 'refused', code, member, collisions }
  }
  if (code === 'claimed_elsewhere') {
    const company = record(record(body)?.company)
    return company && isString(company.id) && isString(company.name)
      ? { status: 'refused', code, company: { id: company.id, name: company.name } }
      : null
  }
  if (code !== 'claim_blocked') return { status: 'refused', code }
  const accounts = arrayOf(record(body)?.accounts, accountOf)
  return accounts === null ? null : { status: 'refused', code, accounts }
}

async function send<T>(
  url: string,
  init: RequestInit,
  parse: (body: unknown) => T | null,
): Promise<Sent<T>> {
  const generation = authGeneration()
  let response: Response
  let text: string
  try {
    response = await fetch(url, { credentials: 'same-origin', signal: authSignal(), ...init })
    text = await response.text()
  } catch {
    return generation === authGeneration() ? { status: 'unavailable' } : { status: 'stale' }
  }
  if (generation !== authGeneration()) return { status: 'stale' }
  if (response.status === 401) {
    reportSessionExpired()
    return { status: 'expired' }
  }
  // The empty body is the discriminator: the one property the layer's refusal and a
  // handler's JSON refusal never share.
  if (response.status === 404 && text === '') return { status: 'denied' }
  // A purge answers `204` with no body; `parse` never sees it.
  if (response.status === 204) return { status: 'ok', body: null as T }
  let body: unknown
  try {
    body = JSON.parse(text)
  } catch {
    return { status: 'unavailable' }
  }
  const error = typeof body === 'object' && body !== null ? (body as { error?: unknown }).error : undefined
  if (response.status === 404 && (error === 'not_found' || error === 'unknown_account')) return { status: 'notFound' }
  if (response.status === 409 && error === 'identity_conflict') return { status: 'conflict', code: 'identity_conflict' }
  if (response.status === 409 && error === 'already_decided') {
    const request = requestOf((body as { request?: unknown }).request)
    return request === null ? { status: 'unavailable' } : { status: 'conflict', code: 'already_decided', request }
  }
  const refused = refusalOf(response.status, error, body)
  if (refused !== null) return refused
  if (!response.ok) return { status: 'unavailable' }
  const parsed = parse(body)
  return parsed === null ? { status: 'unavailable' } : { status: 'ok', body: parsed }
}

const ROLES: readonly string[] = ['owner', 'admin', 'member', 'viewer']
const isRole = (value: unknown): value is Role => typeof value === 'string' && ROLES.includes(value)
const isString = (value: unknown): value is string => typeof value === 'string'
const isNullableString = (value: unknown): value is string | null => value === null || isString(value)

function arrayOf<T>(value: unknown, item: (entry: unknown) => T | null): T[] | null {
  if (!Array.isArray(value)) return null
  const items: T[] = []
  for (const entry of value) {
    const parsed = item(entry)
    if (parsed === null) return null
    items.push(parsed)
  }
  return items
}

function record(value: unknown): Record<string, unknown> | null {
  return typeof value === 'object' && value !== null ? (value as Record<string, unknown>) : null
}

function userOf(value: unknown): AdminUser | null {
  const v = record(value)
  if (v === null) return null
  const { id, email, canCreateCompanies, disabled, platformAdmin, admittedAt, lastSignedInAt } = v
  if (!isString(id) || !isString(email) || !isString(admittedAt) || !isNullableString(lastSignedInAt)) return null
  if (typeof canCreateCompanies !== 'boolean' || typeof disabled !== 'boolean' || typeof platformAdmin !== 'boolean') return null
  const companies = arrayOf(v.companies, (entry) => {
    const c = record(entry)
    return c && isString(c.id) && isString(c.name) && isRole(c.role) ? { id: c.id, name: c.name, role: c.role } : null
  })
  const pendingInvites = arrayOf(v.pendingInvites, (entry) => {
    const i = record(entry)
    return i && isString(i.companyId) && isString(i.companyName) && isRole(i.role) && isString(i.expiresAt)
      ? { companyId: i.companyId, companyName: i.companyName, role: i.role, expiresAt: i.expiresAt }
      : null
  })
  if (companies === null || pendingInvites === null) return null
  return { id, email, canCreateCompanies, disabled, platformAdmin, admittedAt, lastSignedInAt, companies, pendingInvites }
}

function requestOf(value: unknown): AdminRequest | null {
  const v = record(value)
  if (v === null) return null
  const { id, kind, email, requestedAt, message, status, decidedAt, decidedBy } = v
  if (!isString(id) || !isString(email) || !isString(requestedAt)) return null
  if (kind !== 'admission' && kind !== 'companyCreation') return null
  if (status !== 'pending' && status !== 'approved' && status !== 'rejected') return null
  if (!isNullableString(message) || !isNullableString(decidedAt) || !isNullableString(decidedBy)) return null
  const memberships = arrayOf(v.memberships, (entry) => {
    const m = record(entry)
    return m && isString(m.companyName) && isRole(m.role) ? { companyName: m.companyName, role: m.role } : null
  })
  if (memberships === null) return null
  return { id, kind, email, requestedAt, message, memberships, status, decidedAt, decidedBy }
}

export function fetchAdminUsers(): Promise<Sent<AdminUser[]>> {
  return send('/api/admin/users', {}, (body) => arrayOf(record(body)?.users, userOf))
}

export interface RequestLists {
  pending: AdminRequest[]
  decided: AdminRequest[]
}

export function fetchAdminRequests(): Promise<Sent<RequestLists>> {
  return send('/api/admin/requests', {}, (body) => {
    const pending = arrayOf(record(body)?.pending, requestOf)
    const decided = arrayOf(record(body)?.decided, requestOf)
    return pending && decided ? { pending, decided } : null
  })
}

export function decideRequest(id: string, decision: 'approve' | 'reject'): Promise<Sent<AdminRequest>> {
  return send(`/api/admin/requests/${encodeURIComponent(id)}/${decision}`, { method: 'POST' }, (body) => requestOf(record(body)?.request))
}

/** How a company came to exist (`companies.created_via`). */
export type CreatedVia = 'self' | 'admin' | 'signup_demo' | 'unknown'

/** An Owner's deletion request on a frozen company. `purgeAfter` starts purge eligibility. */
export interface Deletion {
  requestedAt: string
  requestedBy: { id: string; email: string | null }
  purgeAfter: string
}

/** One row of `GET /api/admin/companies`. */
export interface AdminCompany {
  id: string
  name: string
  isDemo: boolean
  status: 'active' | 'deletion_requested'
  /** Set exactly when `status` is `deletion_requested` (GRO-138, D7). */
  deletion: Deletion | null
  createdAt: string
  createdVia: CreatedVia
  /** Null when nothing records the creator; the email is null only when no snapshot was kept. */
  creator: { id: string; email: string | null } | null
  owners: string[]
  memberCount: number
  /** Connected ad accounts. */
  accountCount: number
  portfolios: string[]
}

export interface ClaimAccount {
  platformAccountId: string
  name: string
  /** The stored connection to this company. */
  connected: boolean
  /** Discovery no longer sees the account shared. */
  withdrawn: boolean
}

/** `GET /api/admin/companies/{id}`: the list entry with its members and claims. */
export interface AdminCompanyDetail extends AdminCompany {
  members: { email: string; role: Role; joinedAt: string }[]
  claims: { businessId: string; name: string; accounts: ClaimAccount[] }[]
}

const VIA: readonly string[] = ['self', 'admin', 'signup_demo', 'unknown']
const isCount = (value: unknown): value is number => typeof value === 'number' && Number.isInteger(value) && value >= 0

function companyOf(value: unknown): AdminCompany | null {
  const v = record(value)
  if (v === null) return null
  const { id, name, isDemo, status, createdAt, createdVia, memberCount, accountCount } = v
  if (!isString(id) || !isString(name) || typeof isDemo !== 'boolean') return null
  if (status !== 'active' && status !== 'deletion_requested') return null
  if (!isString(createdAt) || !isString(createdVia) || !VIA.includes(createdVia)) return null
  if (!isCount(memberCount) || !isCount(accountCount)) return null
  let creator: AdminCompany['creator'] = null
  if (v.creator !== null) {
    const c = record(v.creator)
    if (c === null || !isString(c.id) || !isNullableString(c.email)) return null
    creator = { id: c.id, email: c.email }
  }
  const owners = arrayOf(v.owners, (o) => (isString(o) ? o : null))
  const portfolios = arrayOf(v.portfolios, (o) => (isString(o) ? o : null))
  if (owners === null || portfolios === null) return null
  let deletion: Deletion | null = null
  if (status === 'deletion_requested') {
    const d = record(v.deletion)
    const by = record(d?.requestedBy)
    if (d === null || by === null || !isString(d.requestedAt) || !isString(d.purgeAfter)) return null
    if (!isString(by.id) || !isNullableString(by.email)) return null
    deletion = { requestedAt: d.requestedAt, requestedBy: { id: by.id, email: by.email }, purgeAfter: d.purgeAfter }
  } else if (v.deletion !== null && v.deletion !== undefined) return null
  return { id, name, isDemo, status, deletion, createdAt, createdVia: createdVia as CreatedVia, creator, owners, memberCount, accountCount, portfolios }
}

function companyDetailOf(value: unknown): AdminCompanyDetail | null {
  const entry = companyOf(value)
  const v = record(value)
  if (entry === null || v === null) return null
  const members = arrayOf(v.members, (m) => {
    const r = record(m)
    return r && isString(r.email) && isRole(r.role) && isString(r.joinedAt) ? { email: r.email, role: r.role, joinedAt: r.joinedAt } : null
  })
  const claims = arrayOf(v.claims, (c) => {
    const r = record(c)
    if (r === null || !isString(r.businessId) || !isString(r.name)) return null
    const accounts = arrayOf(r.accounts, (a) => {
      const x = record(a)
      return x && isString(x.platformAccountId) && isString(x.name) && typeof x.connected === 'boolean' && typeof x.withdrawn === 'boolean'
        ? { platformAccountId: x.platformAccountId, name: x.name, connected: x.connected, withdrawn: x.withdrawn }
        : null
    })
    return accounts === null ? null : { businessId: r.businessId, name: r.name, accounts }
  })
  if (members === null || claims === null) return null
  return { ...entry, members, claims }
}

export function fetchAdminCompanies(): Promise<Sent<AdminCompany[]>> {
  return send('/api/admin/companies', {}, (body) => arrayOf(record(body)?.companies, companyOf))
}

export function fetchAdminCompany(id: string): Promise<Sent<AdminCompanyDetail>> {
  return send(`/api/admin/companies/${encodeURIComponent(id)}`, {}, (body) => companyDetailOf(record(body)?.company))
}

/** What `POST /api/admin/companies` answers on success: the new company's id and stored name. */
export interface CreatedCompany {
  id: string
  name: string
}

/**
 * Creates a real company whose Owner is `ownerId`, a member at once (GRO-137). A refusal
 * comes back as `refused` with `empty_name`, `duplicate_name`, `reserved_name` or
 * `no_eligible_owner`; anything else that is not `ok` leaves the outcome unknown.
 */
export function createAdminCompany(name: string, ownerId: string): Promise<Sent<CreatedCompany>> {
  return send(
    '/api/admin/companies',
    { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ name, ownerId }) },
    (body) => {
      const c = record(record(body)?.company)
      return c && isString(c.id) && isString(c.name) ? { id: c.id, name: c.name } : null
    },
  )
}
/** `POST /api/admin/companies/{id}/rename`: the detail after the commit, or a refusal. */
export function renameCompany(id: string, name: string): Promise<Sent<AdminCompanyDetail>> {
  return send(
    `/api/admin/companies/${encodeURIComponent(id)}/rename`,
    { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ name }) },
    (body) => companyDetailOf(record(body)?.company),
  )
}

/** `POST /api/admin/companies/{id}/restore`, under a new name when `name` is given (GRO-138, D6). */
export function restoreAdminCompany(id: string, name?: string): Promise<Sent<AdminCompanyDetail>> {
  return send(
    `/api/admin/companies/${encodeURIComponent(id)}/restore`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(name === undefined ? {} : { name }),
    },
    (body) => companyDetailOf(record(body)?.company),
  )
}

/**
 * Purges a frozen company at once (GRO-139, D7). `name` is the typed confirmation; the
 * server compares it to the company's name.
 */
export function purgeAdminCompany(id: string, name: string): Promise<Sent<null>> {
  return send(
    `/api/admin/companies/${encodeURIComponent(id)}/purge`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name }),
    },
    () => null,
  )
}

/** What `/admin` shows a Meta request as; waiting is a queued request with a reason. */
export type MetaRequestState = 'queued' | 'running' | 'waiting' | 'done' | 'failed'
export type WaitReason = 'budget' | 'throttled' | 'backoff'

/** One Meta request (GRO-141, D8). */
export interface MetaRequest {
  id: string
  state: MetaRequestState
  requestedAt: string
  requestedByEmail: string | null
  startedAt: string | null
  finishedAt: string | null
  runs: number
  failures: number
  lastError: string | null
  waitReason: WaitReason | null
  nextAttemptAt: string | null
  pages: number
  stagedAccounts: number
  stagedPortfolios: number
}

/** A Meta account by its id and name: one a portfolio lists, or one that blocks an unclaim. */
export interface PortfolioAccount {
  platformAccountId: string
  name: string
}

/** A complete collection's facts (GRO-145, D9). */
export interface CompleteFacts {
  windowSince: string
  windowUntil: string
  timezoneName: string
  finishedAt: string
  coveredDays: number
  /** `omni_purchase` observed (`true`) or absent (`false`). */
  omniPurchase: boolean
  mainActionTypes: string[]
  /** Each objective and its campaign count, largest first; `unknown` for none. */
  objectives: Array<{ objective: string; campaigns: number }>
  /** The listing stopped at 500 campaigns. */
  objectivesTruncated: boolean
}

/** An account's conversion facts: the latest complete collection, the running one, the latest request. */
export interface FactsSummary {
  complete: CompleteFacts | null
  /** Days written of 90 by the running collection. */
  collectingDays: number | null
  request: MetaRequest | null
}

/** A listed account with its conversion facts. */
export interface ListedAccount extends PortfolioAccount {
  facts: FactsSummary
}

/** One portfolio the published discovery lists. */
export interface MetaPortfolio {
  businessId: string
  businessName: string | null
  accounts: number
  connected: number
  claimedBy: { id: string; name: string } | null
  /** The accounts discovery lists for it, not withdrawn, by name. */
  listed: ListedAccount[]
}

/** `GET /api/admin/portfolios`. */
export interface PortfoliosOverview {
  lastDiscoveryAt: string | null
  portfolios: MetaPortfolio[]
  rediscovery: MetaRequest | null
}

const STATES: readonly string[] = ['queued', 'running', 'waiting', 'done', 'failed']
const REASONS: readonly string[] = ['budget', 'throttled', 'backoff']

function metaRequestOf(value: unknown): MetaRequest | null {
  const v = record(value)
  if (v === null) return null
  const { id, state, requestedAt, requestedByEmail, startedAt, finishedAt, lastError, waitReason, nextAttemptAt } = v
  if (!isString(id) || !isString(state) || !STATES.includes(state) || !isString(requestedAt)) return null
  if (!isNullableString(requestedByEmail) || !isNullableString(startedAt) || !isNullableString(finishedAt)) return null
  if (!isNullableString(lastError) || !isNullableString(nextAttemptAt)) return null
  if (waitReason !== null && !(isString(waitReason) && REASONS.includes(waitReason))) return null
  const { runs, failures, pages, stagedAccounts, stagedPortfolios } = v
  if (!isCount(runs) || !isCount(failures) || !isCount(pages) || !isCount(stagedAccounts) || !isCount(stagedPortfolios)) return null
  return {
    id, state: state as MetaRequestState, requestedAt, requestedByEmail, startedAt, finishedAt, runs, failures, lastError,
    waitReason: waitReason as WaitReason | null, nextAttemptAt, pages, stagedAccounts, stagedPortfolios,
  }
}

function accountOf(value: unknown): PortfolioAccount | null {
  const a = record(value)
  return a && isString(a.platformAccountId) && isString(a.name) ? { platformAccountId: a.platformAccountId, name: a.name } : null
}

function completeOf(value: unknown): CompleteFacts | null {
  const v = record(value)
  if (v === null) return null
  const { windowSince, windowUntil, timezoneName, finishedAt, coveredDays, omniPurchase, objectivesTruncated } = v
  if (!isString(windowSince) || !isString(windowUntil) || !isString(timezoneName) || !isString(finishedAt)) return null
  if (!isCount(coveredDays) || typeof omniPurchase !== 'boolean' || typeof objectivesTruncated !== 'boolean') return null
  const mainActionTypes = arrayOf(v.mainActionTypes, (t) => (isString(t) ? t : null))
  const objectives = arrayOf(v.objectives, (o) => {
    const r = record(o)
    return r && isString(r.objective) && isCount(r.campaigns) ? { objective: r.objective, campaigns: r.campaigns } : null
  })
  if (mainActionTypes === null || objectives === null) return null
  return { windowSince, windowUntil, timezoneName, finishedAt, coveredDays, omniPurchase, mainActionTypes, objectives, objectivesTruncated }
}

function factsOf(value: unknown): FactsSummary | null {
  const v = record(value)
  if (v === null) return null
  const complete = v.complete === null ? null : completeOf(v.complete)
  const request = v.request === null ? null : metaRequestOf(v.request)
  if ((v.complete !== null && complete === null) || (v.request !== null && request === null)) return null
  if (v.collectingDays !== null && !isCount(v.collectingDays)) return null
  return { complete, collectingDays: v.collectingDays, request }
}

function listedOf(value: unknown): ListedAccount | null {
  const account = accountOf(value)
  const facts = factsOf(record(value)?.facts)
  return account && facts ? { ...account, facts } : null
}

function portfolioOf(value: unknown): MetaPortfolio | null {
  const v = record(value)
  if (v === null) return null
  const { businessId, businessName, accounts, connected } = v
  if (!isString(businessId) || !isNullableString(businessName) || !isCount(accounts) || !isCount(connected)) return null
  let claimedBy: MetaPortfolio['claimedBy'] = null
  if (v.claimedBy !== null) {
    const c = record(v.claimedBy)
    if (c === null || !isString(c.id) || !isString(c.name)) return null
    claimedBy = { id: c.id, name: c.name }
  }
  const listed = arrayOf(v.listed, listedOf)
  if (listed === null) return null
  return { businessId, businessName, accounts, connected, claimedBy, listed }
}

function overviewOf(body: unknown): PortfoliosOverview | null {
  const v = record(body)
  if (v === null || !isNullableString(v.lastDiscoveryAt)) return null
  const portfolios = arrayOf(v.portfolios, portfolioOf)
  const rediscovery = v.rediscovery === null ? null : metaRequestOf(v.rediscovery)
  if (portfolios === null || (v.rediscovery !== null && rediscovery === null)) return null
  return { lastDiscoveryAt: v.lastDiscoveryAt, portfolios, rediscovery }
}

export function fetchPortfolios(): Promise<Sent<PortfoliosOverview>> {
  return send('/api/admin/portfolios', {}, overviewOf)
}

/**
 * `POST /api/admin/portfolios/{businessId}/claim` or `/unclaim` for `companyId` (GRO-144,
 * D4): the overview after the commit, or a refusal.
 */
export function changeClaim(action: 'claim' | 'unclaim', businessId: string, companyId: string): Promise<Sent<PortfoliosOverview>> {
  return send(
    `/api/admin/portfolios/${encodeURIComponent(businessId)}/${action}`,
    { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ companyId }) },
    overviewOf,
  )
}

/** Opens a rediscovery, or answers the one already active. */
export function requestRediscovery(): Promise<Sent<MetaRequest>> {
  return send('/api/admin/portfolios/rediscover', { method: 'POST' }, (body) => metaRequestOf(record(body)?.request))
}

/** A new request with a failed one's operation and scope, or the one already active. */
export function retryMetaRequest(id: string): Promise<Sent<MetaRequest>> {
  return send(`/api/admin/meta-requests/${encodeURIComponent(id)}/retry`, { method: 'POST' }, (body) => metaRequestOf(record(body)?.request))
}

/** A new facts request for the account, or the one already active (GRO-145, D2). */
export function requestFacts(platformAccountId: string): Promise<Sent<MetaRequest>> {
  return send(`/api/admin/accounts/${encodeURIComponent(platformAccountId)}/facts`, { method: 'POST' }, (body) => metaRequestOf(record(body)?.request))
}
